/** Collapsed and expanded nav share one grid so nothing shifts on hover-expand. */
export const SIDEBAR_RAIL_WIDTH = "w-11"
export const SIDEBAR_TRACK_PADDING = "px-1.5"
export const SIDEBAR_ITEM_GAP = "gap-1"
export const SIDEBAR_ITEM_BOX = "h-8 rounded-lg"
export const SIDEBAR_ITEM_LEADING_PADDING = "pl-2"

/**
 * The rail is a flow child so it is inset by padding; the hover panel is
 * absolute so it is inset by top/bottom. Absolute insets resolve against the
 * padding box, so the padding does NOT apply to the panel and these two have to
 * name the same step or every item shifts on expand.
 */
export const SIDEBAR_VERTICAL_INSET = "py-1"
export const SIDEBAR_PANEL_INSET = "inset-y-1"

export const sidebarTrackStyles = `flex w-full flex-col ${SIDEBAR_ITEM_GAP} ${SIDEBAR_TRACK_PADDING}`

/**
 * The workspace row hovers like a nav row, so it borrows the item box; the
 * collapsed logo names the same height or the avatar drops on hover-expand.
 */
export const sidebarWorkspaceRowStyles = `w-full ${SIDEBAR_ITEM_BOX} pl-1`
export const sidebarWorkspaceLogoStyles =
	"relative flex h-8 items-center justify-center"

export const sidebarItemStyles = (collapsed: boolean) =>
	[
		"group/navlink flex items-center text-secondary",
		SIDEBAR_ITEM_BOX,
		SIDEBAR_ITEM_LEADING_PADDING,
		collapsed ? "relative pr-2" : "pr-1.5",
	].join(" ")

/**
 * A hidden nav keeps this sliver of page background, so the content card stays
 * inset from the viewport edge by the same step as SIDEBAR_CONTENT_INSET's
 * right padding.
 */
export const SIDEBAR_HIDDEN_GUTTER_WIDTH = "w-2"
export const SIDEBAR_CONTENT_INSET = "py-1 pl-1 pr-1 lg:py-2 lg:pl-0 lg:pr-2"

/**
 * While the sidebar is hidden, its toggle floats over the card's top bar: the
 * 28px button centred in the 48px bar, its glyph on the bar's 17.5px text inset.
 * Offsets count from the rail's left edge: the w-2 gutter plus the card inset.
 */
export const HIDDEN_NAV_TOGGLE_POSITION =
	"absolute left-[22.5px] top-3.5 lg:left-[18.5px] lg:top-[18px]"
/** Read by each page's leftmost top bar (NAV_TOGGLE_CLEARANCE) to move its title past the toggle. */
export const HIDDEN_NAV_TOGGLE_INSET = "[--nav-toggle-inset:28px]"
/**
 * The peeking nav opens 4px under the toggle, so the top bar and the toggle stay
 * in view. Like Linear's it grows out of the screen's left edge and stops on the
 * card's bottom inset.
 */
export const HIDDEN_NAV_PEEK_INSET =
	"left-0 top-[46px] bottom-1 lg:top-[50px] lg:bottom-2"

/**
 * Column and nav share one timing so the nav's edge rides the card's. Arbitrary
 * properties: tailwindcss-animate makes duration-[…] and ease-[…] ambiguous, and Tailwind drops them.
 */
const NAV_DOCK_MOTION =
	"[transition-duration:250ms] [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
export const NAV_COLUMN_TRANSITION = `transition-[width,min-width] ${NAV_DOCK_MOTION}`

const FLOATING_NAV = `absolute rounded-r-xl bg-gray-100 shadow-xl ring-1 ring-black/10 lg:py-1 dark:bg-gray-950 dark:shadow-black dark:ring-white/15 ${HIDDEN_NAV_PEEK_INSET}`

const NAV_SLIDE = `transition-[transform,opacity] ${NAV_DOCK_MOTION}`

/**
 * Each slide keeps the frame it started in, so nothing jumps midway; a peek
 * still on screen docks in place ("docked-from-peek") instead of replaying the slide.
 */
export type MainNavFrame =
	| "docked"
	| "docked-from-peek"
	| "hidden"
	| "peek"
	| "unpeeked"

const MAIN_NAV_FRAMES: Record<MainNavFrame, string> = {
	docked: `${NAV_SLIDE} h-full translate-x-0 opacity-100`,
	"docked-from-peek": "h-full translate-x-0 opacity-100",
	hidden: `${NAV_SLIDE} absolute inset-y-0 left-0 -translate-x-full opacity-0`,
	peek: `${NAV_SLIDE} ${FLOATING_NAV} translate-x-0 opacity-100`,
	unpeeked: `${NAV_SLIDE} ${FLOATING_NAV} -translate-x-full opacity-0`,
}

export const mainNavFrameStyles = (frame: MainNavFrame) =>
	MAIN_NAV_FRAMES[frame]

/** `floating`: the nav is on screen as a peek, opening, open, or still sliding away. */
export const mainNavFrame = ({
	hidden,
	peek,
	floating,
}: {
	hidden: boolean
	peek: boolean
	floating: boolean
}): MainNavFrame => {
	if (!hidden) return floating ? "docked-from-peek" : "docked"
	if (peek) return "peek"
	return floating ? "unpeeked" : "hidden"
}

/**
 * The phone nav floats over the page so the blur has something to blur, so
 * scroll regions clear it with padding instead of the layout reserving height.
 * Every class here is written out in full: Tailwind scans source text, so a
 * value assembled from a template literal never reaches the stylesheet.
 */
export const MOBILE_BAR_CLEARANCE =
	"max-sm:pb-[calc(4.75rem+env(safe-area-inset-bottom))]"
export const MOBILE_BAR_STACK_ABOVE =
	"max-sm:bottom-[calc(4.75rem+env(safe-area-inset-bottom))]"

export const mobileBarShellStyles = [
	"max-sm:pointer-events-none max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-40",
	"max-sm:h-auto max-sm:justify-center max-sm:px-3 max-sm:pb-[env(safe-area-inset-bottom)] max-sm:pt-0",
].join(" ")

/**
 * The inset white line along the top edge is what reads as glass rather than
 * flat translucency, and it rides in the shadow so it costs no layout pixel.
 */
export const mobileBarPillStyles = [
	"max-sm:pointer-events-auto max-sm:mx-0 max-sm:mb-2 max-sm:h-auto max-sm:!w-full max-sm:!opacity-100",
	"max-sm:flex-row max-sm:items-center max-sm:justify-between max-sm:gap-0.5 max-sm:overflow-visible",
	// Equal inset on all four sides: a stadium chip only nests cleanly inside a
	// stadium pill when the two arcs stay concentric.
	"max-sm:rounded-full max-sm:p-1",
	"max-sm:bg-white/55 max-sm:backdrop-blur-2xl max-sm:backdrop-saturate-[1.8]",
	"max-sm:ring-1 max-sm:ring-black/5",
	"max-sm:shadow-[0_8px_32px_rgba(0,0,0,0.16),inset_0_1px_0_rgba(255,255,255,0.55)]",
	"max-sm:dark:bg-gray-900/55 max-sm:dark:ring-white/10",
	"max-sm:dark:shadow-[0_8px_32px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.12)]",
].join(" ")

/**
 * Apple sizes a tab bar at 49pt and asks for a 44pt minimum tap target, so a tab
 * is 44 tall and the pill's own padding brings it to roughly that 49.
 */
export const MOBILE_NAV_TAB =
	"max-sm:h-11 max-sm:min-w-11 max-sm:flex-1 max-sm:justify-center max-sm:rounded-full max-sm:px-0"

/** Apple caps a tab bar at five tabs, so inbox and the bell leave room for three. */
export const MOBILE_NAV_MAX_TABS = 5
export const MOBILE_NAV_MAX_MIDDLE = MOBILE_NAV_MAX_TABS - 2

export const MOBILE_NAV_ITEM_WIDTH = 44
/** Inbox, the bell and the expand button hold their slots. */
export const MOBILE_NAV_PINNED_WIDTH = 132
export const MOBILE_NAV_EXPAND_WIDTH = 44
export const MOBILE_NAV_TRACK_PADDING = 16

/** How many of the middle items fit, counting the expand button only when it renders. */
export function visibleMobileNavCount(barWidth: number, total: number) {
	if (barWidth <= 0) return total
	const room = barWidth - MOBILE_NAV_TRACK_PADDING - MOBILE_NAV_PINNED_WIDTH
	if (Math.floor(room / MOBILE_NAV_ITEM_WIDTH) >= total) return total
	const roomBesideExpand = room - MOBILE_NAV_EXPAND_WIDTH
	return Math.max(0, Math.floor(roomBesideExpand / MOBILE_NAV_ITEM_WIDTH))
}

/**
 * Two layout rows can open one page (Agent sits in Personal and Workspace), and
 * the phone bar has no section headers to tell them apart, so each href shows once.
 */
export function uniquePhoneNavItems<T extends { href: string }>(
	shown: T[],
	hidden: T[],
): { shown: T[]; hidden: T[] } {
	const seen = new Set<string>()
	const keep = (item: T) => {
		if (seen.has(item.href)) return false
		seen.add(item.href)
		return true
	}
	return { shown: shown.filter(keep), hidden: hidden.filter(keep) }
}
